// Sentry error tracking — gated by cookie consent.
// Init NIE pri load — len ked user accept-ne consent v CookieBanner.jsx,
// alebo automaticky pri restart browseru ak je consent uz v localStorage.
//
// PostHog NEpouzivame — analytics riesi vlastny lib/analytics.js + Supabase
// `analytics_events` tabulka (opt-in benchmarking).
//
// Decision: docs/decisions/2026-05-08-sentry-error-tracking.md

import * as Sentry from '@sentry/react';

const COOKIE_CONSENT_KEY = 'cd:cookie-consent-v1';

let sentryInited = false;

function hasConsent() {
  try {
    return Boolean(localStorage.getItem(COOKIE_CONSENT_KEY));
  } catch {
    return false;
  }
}

function redactQueryString(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, window.location.origin);
    const SENSITIVE = ['email', 'ico', 'dic', 'code', 'token', 'access_token', 'refresh_token'];
    SENSITIVE.forEach((key) => {
      if (u.searchParams.has(key)) u.searchParams.set(key, '[Filtered]');
    });
    return u.toString();
  } catch {
    return url;
  }
}

function scrubPii(event) {
  if (!event) return event;

  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  if (event.request) {
    if (event.request.headers) {
      delete event.request.headers.cookie;
      delete event.request.headers.Cookie;
      delete event.request.headers.authorization;
      delete event.request.headers.Authorization;
    }
    if (event.request.url) {
      event.request.url = redactQueryString(event.request.url);
    }
    if (event.request.query_string && typeof event.request.query_string === 'string') {
      event.request.query_string = redactQueryString(`?${event.request.query_string}`).replace(/^.*\?/, '');
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs.forEach((b) => {
      if (b?.data?.url) b.data.url = redactQueryString(b.data.url);
    });
  }

  return event;
}

export function initSentry() {
  if (sentryInited) return;

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console -- intentional warning, gated by NODE_ENV
      console.warn('[observability] REACT_APP_SENTRY_DSN missing, Sentry skipped.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'production',
    release: 'todue@2026-05-08',
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    maxBreadcrumbs: 30,
    ignoreErrors: [
      /ResizeObserver loop/,
      /Non-Error promise rejection captured/,
      'Network request failed',
      'AbortError',
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.dedupeIntegration(),
    ],
    beforeSend(event) {
      return scrubPii(event);
    },
  });

  sentryInited = true;
}

export function maybeInitSentryFromConsent() {
  if (hasConsent()) initSentry();
}

export function captureException(error, context) {
  if (!sentryInited) return;
  Sentry.captureException(error, context);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
